const {dayColumns, summarizeDays, formatCipher} = require('../../utils/timesheetDaySummary');
const components = line => line.calculation_details && line.calculation_details.components || [];
const itr = line => components(line).some(part => part.employee_category === 'itr');
const compare = (a, b) => Number(itr(b)) - Number(itr(a))
    || String(a.employee_name_snapshot).localeCompare(String(b.employee_name_snapshot), 'ru', {numeric: true});

function groups(lines, byObject) {
    if (!byObject) return [{key: 'all', name: '', lines: lines.slice().sort(compare)}];
    const result = new Map();
    lines.forEach(line => {
        const sources = components(line), ids = Array.from(new Set(sources.map(part => Number(part.building_id) || 0)));
        if (!ids.length) ids.push(0);
        ids.forEach(id => {
            const parts = sources.filter(part => (Number(part.building_id) || 0) === id);
            if (!result.has(id)) result.set(id, {key: String(id), name: (parts[0] || {}).object_name || 'Без источника в комплекте', lines: []});
            result.get(id).lines.push({...line, _allComponents: line._allComponents || sources, _objectView: true,
                calculation_details: {...line.calculation_details, components: parts}});
        });
    });
    return Array.from(result.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru', {numeric: true}))
        .map(group => ({...group, lines: group.lines.sort(compare)}));
}

function days(line) {
    const seen = new Set();
    const parts = components(line).filter(part => {
        if (!part.revision_id || !part.timesheet_id) return true;
        const key = `${part.revision_id}:${part.timesheet_id}:${part.line_id || (part.source_details || {}).line_id || 0}`;
        if (seen.has(key)) return false;
        seen.add(key); return true;
    });
    if (!parts.length || parts.some(part => !part.source_details || !Array.isArray(part.source_details.days))) return null;
    return summarizeDays(parts.reduce((records, part) => records.concat(part.source_details.days.map(day => ({
        ...day, key: `${part.building_id}:${day.date}`, policy: part.source_details.day_counting || {}
    }))), [])).totals;
}

function cipher(line) { return formatCipher(days(line)); }

const unknownSortValue = value => value == null || value === '' || typeof value === 'number' && !Number.isFinite(value)
    || Array.isArray(value) && (!value.length || value.every(unknownSortValue));

function compareSortValues(a, b, direction = 'asc') {
    const sign = direction === 'desc' ? -1 : 1;
    const emptyA = unknownSortValue(a), emptyB = unknownSortValue(b);
    // Empty lists and non-finite numbers are unknown too. Zero is always a real value.
    if (emptyA || emptyB) return emptyA ? (emptyB ? 0 : 1) : -1;
    if (Array.isArray(a) || Array.isArray(b)) {
        a = Array.isArray(a) ? a : [a]; b = Array.isArray(b) ? b : [b];
        for (let index = 0; index < Math.min(a.length, b.length); index++) {
            const result = compareSortValues(a[index], b[index], direction);
            if (result) return result;
        }
        return sign * (a.length - b.length);
    }
    return sign * (typeof a === 'number' && typeof b === 'number' ? a - b
        : String(a).localeCompare(String(b), 'ru', {numeric: true, sensitivity: 'base'}));
}

function cipherSortValue(line) {
    const totals = days(line);
    if (!totals) return null;
    const known = dayColumns.map(column => column.key);
    return known.map(key => Number(totals[key] || 0)).concat(Object.keys(totals).filter(key => !known.includes(key))
        .sort((a, b) => a.localeCompare(b, 'ru')).map(key => [key, Number(totals[key] || 0)]));
}

function sortLines(lines, value, direction = 'asc', itrFirst = true) {
    const entries = lines.map(line => ({line, value: value(line)}));
    return entries.sort((a, b) => (itrFirst ? Number(itr(b.line)) - Number(itr(a.line)) : 0)
        || compareSortValues(a.value, b.value, direction)
        || String(a.line.employee_name_snapshot || '').localeCompare(String(b.line.employee_name_snapshot || ''), 'ru', {numeric: true, sensitivity: 'base'})
        || String(a.line.id).localeCompare(String(b.line.id), 'ru', {numeric: true})).map(entry => entry.line);
}

// Compare the two displayed amounts, never a monthly adjustment copied into an object slice.
function amountDifference(calculated, payable) {
    const known = value => (typeof value === 'number' || typeof value === 'string' && value.trim() !== '') && Number.isSafeInteger(Number(value));
    if (!known(calculated) || !known(payable)) return null;
    const base = Number(calculated), minor = Number(payable) - base;
    if (!Number.isSafeInteger(minor) || minor === 0) return null;
    return {minor, percent: base === 0 ? null : minor / Math.abs(base) * 100,
        large: base === 0 || Math.abs(minor) >= Math.ceil(Math.abs(base) / 10),
        label: minor > 0 ? 'Выше расчёта' : 'Ниже расчёта'};
}

function differenceReasons(line) {
    const overrides = line.manual_overrides || {}, payload = line.source_payload || {};
    const reasonText = value => typeof value === 'string' && value.trim() || null;
    const legacy = ['final_amount_minor', 'accepted_minutes', 'accepted_days'].some(key => Object.prototype.hasOwnProperty.call(overrides, key));
    if (legacy) return [{object: '', reason: reasonText(payload.manual_edit_reason), author: (line.reason_authors || {}).month || null}];
    const ids = new Set(((line.calculation_details || {}).components || []).map(part => Number(part.building_id) || 0));
    const objects = ((line.source_calculation || {}).objects || []).filter(object =>
        (!line._objectView || ids.has(Number(object.building_id) || 0)) && amountDifference(object.amount_minor, object.final_amount_minor));
    const reasons = objects.map(object => ({
        object: line._objectView ? '' : object.object_name || `Объект №${object.building_id}`,
        reason: reasonText(object.reason), author: ((line.reason_authors || {}).objects || {})[object.building_id] || null
    }));
    return reasons.length ? reasons : [{object: '', reason: null}];
}

module.exports = {dayColumns, groups, days, cipher, cipherSortValue, itr, sortLines, compareSortValues, amountDifference, differenceReasons};
